import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { AppConfig } from "../app.config";
import { LoggingService } from "./LoggingService";
import { AnalyzeCSVRequest } from "../requests/analyzeCSVRequest";
import { AuthenticationService } from "./AuthenticationService";
import { blobToBase64 } from "../helpers/blob.utils";
import { catchError, of, throwError } from "rxjs";
import { BasicResponse } from "../responses/basicResponse";
import { GenerateTransformationRequest } from "../requests/generateTransformationRequest";
import { FileDefinition } from "../models/fileDefinition";
import { TestPromptRequest } from "../requests/testPromptRequest";
import { BasicRequest } from "../requests/basicRequest";

@Injectable({
    providedIn: 'root'
  })
  export class BedrockApiService {

    constructor(private httpClient: HttpClient, private appConfig:AppConfig, private logger:LoggingService, private authService:AuthenticationService) { 
    }
  
    get serviceURL():string {
      return this.appConfig.getConfig("bedrockapi_external_url") + '/api/bedrock/';
    }
    get optionsJson(){
      return this.appConfig.optionsJson;
    }
    get optionsForm(){
      return this.appConfig.optionsForm;
    }

    public async analyzeFile(file:File){
      let r = new AnalyzeCSVRequest();
      r.requestorId=this.authService.currentUserValue!.id;
      const base64WithPrefix = await blobToBase64(file) as string;
      // Remove "data:application/...;base64," prefix to get pure base64
      r.fileBase64 = base64WithPrefix.split(',')[1] || base64WithPrefix;
      r.fileName=file.name;
      this.logger.log("Uploading file to analyze");
      return this.httpClient.post<any>(this.serviceURL+`analyzeFile`, r, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
    }

    public async generateTransformation(sourceFileDefinition:FileDefinition, targetFileDefinition:FileDefinition|undefined, targetFile:File|undefined, context:string, includeDescription:boolean=false, strictValidation:boolean=false){
      let r = new GenerateTransformationRequest();
      r.requestorId=this.authService.currentUserValue!.id;
      r.sourceFileDefinition = FileDefinition.mapToAnalyzer(sourceFileDefinition);
      if(targetFileDefinition) r.targetFileDefinition = FileDefinition.mapToAnalyzer(targetFileDefinition);
      if(targetFile){
        const base64WithPrefix = await blobToBase64(targetFile) as string;
        // Remove "data:application/...;base64," prefix to get pure base64
        r.targetFileContent = base64WithPrefix.split(',')[1] || base64WithPrefix;
        r.targetFileName = targetFile.name;
      }
      r.includeDescription = includeDescription;
      r.strictValidation = strictValidation;
      r.context = context;
      this.logger.log("Generate transformation");
      return this.httpClient.post<any>(this.serviceURL+`generateTransformation`, r, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
    }

    //starts an AI prompt test with a test file; waits up to ~20s on the server, then returns state 'pending' => poll getTestPrompt
    public testPrompt(file:File, instructions:string, moovDefinitionId:number|undefined, outputFileDefinitionId?:number){
      let r = new TestPromptRequest();
      r.requestorId=this.authService.currentUserValue!.id;
      r.requestorEntityId=this.authService.entityContextId;
      r.requestorEntityType=this.authService.entityContextType;
      r.instructions=instructions;
      //billing: tests of customer owned definitions are charged; for a new definition the customer it is created for
      r.moovDefinitionId=moovDefinitionId;
      r.customerId=this.authService.customerContext?.id;
      r.outputFileDefinitionId=outputFileDefinitionId;
      let body = JSON.stringify(r);
      const formData: FormData = new FormData();
      formData.append('requestString', body);
      formData.append('files', file, file.name);
      this.logger.log("Uploading file to test AI prompt");
      return this.httpClient.post<any>(this.serviceURL+`testPrompt`, formData, this.optionsForm).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
    }

    public getTestPrompt(testId:string){
      const requestorEntityId=this.authService.entityContextId;
      const requestorEntityType=this.authService.entityContextType;
      return this.httpClient.get<any>(this.serviceURL+'testPrompt/' + testId + (requestorEntityId?('/' +requestorEntityId):'') + (requestorEntityType?('/' + requestorEntityType):''), this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
    }

    //starts an AI worker in the background, so a following test does not wait for it to start up
    public testPromptWarmup(){
      let r = new BasicRequest();
      r.requestorId=this.authService.currentUserValue!.id;
      r.requestorEntityId=this.authService.entityContextId;
      r.requestorEntityType=this.authService.entityContextType;
      return this.httpClient.post<any>(this.serviceURL+`testPromptWarmup`, r, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
    }

    private createBasicResponseError(error: HttpErrorResponse){
        let br = new BasicResponse();
        br.success=false;
        br.message='Backend error - please try again later';
        return br;
    }

    private handleError(error: HttpErrorResponse) {
        if (error.error instanceof ErrorEvent) {
        // A client-side or network error occurred. Handle it accordingly.
        console.error('An error occurred:', error.error.message);
        } else {
        // The backend returned an unsuccessful response code.
        // The response body may contain clues as to what went wrong,
        console.error(
            `Backend returned code ${error.status}, ` +
            `body was: ${JSON.stringify(error.error)}`);
        }
        // return an observable with a user-facing error message
        return throwError(()=>(error));
    }
  }